import React from "react";
import { Box, HStack, Icon, IconButton, Text, Tooltip } from "@chakra-ui/react";
import { LuMaximize2, LuMinimize2 } from "react-icons/lu";

export interface ArtifactTab {
  id: string;
  label: string;
  icon: React.ElementType;
  iconSize?: number;
}

export interface ArtifactPanelHeaderProps {
  /** Icon component for the artifact type */
  icon: React.ElementType;
  /** Background color token for the icon badge, e.g. "purple.600", "blue.600" */
  iconBg: string;
  /** Icon color inside badge — defaults to "white" */
  iconColor?: string;
  /** Artifact name, displayed truncated if long */
  name: string;
  /** Short human-readable type label shown below the name in muted small text */
  typeLabel: string;
  /** Tab definitions */
  tabs: ArtifactTab[];
  /** Currently active tab id */
  activeTab: string;
  /** Called when a tab is clicked */
  onTabChange: (id: string) => void;
  /** Right-side action buttons — each artifact passes its own */
  actions?: React.ReactNode;
  /** Whether the artifact panel is currently expanded to full width */
  isExpanded?: boolean;
  /** Called when the expand/collapse toggle button is clicked */
  onExpandToggle?: () => void;
}

const ArtifactPanelHeader: React.FC<ArtifactPanelHeaderProps> = ({
  icon,
  iconBg,
  iconColor = "white",
  name,
  typeLabel,
  tabs,
  activeTab,
  onTabChange,
  actions,
  isExpanded = false,
  onExpandToggle,
}) => {
  return (
    <Box bg="white" flexShrink={0}>
      {/* Identity: icon (full height) + name / type stack + actions */}
      <HStack
        px={3}
        pt={2.5}
        pb={2}
        justify="space-between"
        align="stretch"
        spacing={0}
        gap={3}
      >
        <HStack flex={1} minW={0} align="stretch" spacing={0} gap={2.5}>
          <Box
            display="flex"
            alignItems="center"
            alignSelf="stretch"
            justifyContent="center"
            px={1.5}
            py={1}
            borderRadius="md"
            bg={iconBg}
            flexShrink={0}
            minH="40px"
          >
            <Icon as={icon} boxSize={7} color={iconColor} />
          </Box>
          <Box minW={0} flex={1} py={0.5} display="flex" flexDir="column" justifyContent="center" gap={0.5}>
            <Text
              fontSize="sm"
              fontWeight="700"
              lineHeight="1.25"
              color="gray.800"
              m={0}
              isTruncated
              textAlign="left"
            >
              {name}
            </Text>
            <Text
              fontSize="xs"
              color="gray.600"
              fontWeight="600"
              letterSpacing="wide"
              lineHeight="1.2"
              m={0}
              isTruncated
              textAlign="left"
            >
              {typeLabel}
            </Text>
          </Box>
        </HStack>

        <HStack spacing={1} flexShrink={0} alignSelf="flex-start" pt={0.5}>
          {actions}
          {onExpandToggle && (
            <Tooltip
              label={isExpanded ? "Restore split view" : "Expand to full width"}
              placement="bottom"
              hasArrow
              fontSize="xs"
            >
              <IconButton
                aria-label={isExpanded ? "Restore split view" : "Expand to full width"}
                icon={<Icon as={isExpanded ? LuMinimize2 : LuMaximize2} boxSize={4} />}
                size="xs"
                variant="ghost"
                color="gray.700"
                _hover={{ color: "purple.600", bg: "purple.50" }}
                onClick={onExpandToggle}
              />
            </Tooltip>
          )}
        </HStack>
      </HStack>

      {/* Tab bar */}
      <HStack
        spacing={0}
        borderBottom="1px solid"
        borderColor="gray.200"
        bg="white"
        px={2}
        width="100%"
      >
        {tabs.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <HStack
              key={tab.id}
              spacing={1.5}
              px={3}
              py={1.5}
              cursor="pointer"
              borderBottom="2px solid"
              borderColor={isActive ? "purple.500" : "transparent"}
              color={isActive ? "purple.600" : "gray.500"}
              _hover={{ color: isActive ? "purple.600" : "gray.700" }}
              transition="all 0.15s"
              onClick={() => onTabChange(tab.id)}
              mb="-1px"
            >
              <Icon as={tab.icon} boxSize={tab.iconSize ?? 4} />
              <Text fontSize="sm" fontWeight={isActive ? "700" : "500"} mb={0}>
                {tab.label}
              </Text>
            </HStack>
          );
        })}
      </HStack>
    </Box>
  );
};

export default ArtifactPanelHeader;
